import type { MessageKey } from '@dijitalapp/i18n';
import type { AssignableDto, EventDto, EventResponse, PlannerScope, TaskDto } from '@dijitalapp/shared';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat } from '../lib/chat-context';
import { useShell } from './Shell';

type Tab = 'calendar' | 'tasks';
interface ScopeOption {
  key: string;
  scope: PlannerScope;
  label: string;
}

/** Where the user may add items: personal, spaces they belong to, and their (not end-to-end encrypted) groups. */
function useScopeOptions(kind: 'event' | 'task'): ScopeOption[] {
  const { i18n } = useApp();
  const { spaces } = useShell();
  const chat = useChat();
  return useMemo(() => {
    const admin = (r: string | undefined) => r === 'owner' || r === 'admin';
    const options: ScopeOption[] = [{ key: 'personal', scope: { type: 'personal' }, label: i18n.t('planner.personal') }];
    for (const s of spaces) {
      if (s.type === 'personal') continue;
      if (kind === 'event' ? !admin(s.role) : s.role === 'guest') continue;
      options.push({ key: `space:${s.id}`, scope: { type: 'space', id: s.id }, label: `▦ ${s.name}` });
    }
    for (const c of chat.conversations) {
      if (c.type !== 'group' || c.e2ee || !c.group) continue;
      if (c.group.onlyAdminsCanSend && !admin(c.group.myRole)) continue;
      options.push({ key: `group:${c.id}`, scope: { type: 'group', id: c.id }, label: `👥 ${c.group.title}` });
    }
    return options;
  }, [spaces, chat.conversations, kind, i18n]);
}

/** `/calendar`: the user's calendar (personal, spaces, groups) and tasks. */
export function PlannerPage() {
  const { i18n, config } = useApp();
  const { t } = i18n;
  const calendarOn = Boolean(config?.flags.calendar);
  const tasksOn = Boolean(config?.flags.tasks);
  const [tab, setTab] = useState<Tab>(calendarOn ? 'calendar' : 'tasks');

  return (
    <div className="content-inner">
      <h1>{t('planner.title')}</h1>
      <div className="row" role="tablist">
        {calendarOn && (
          <button type="button" role="tab" aria-selected={tab === 'calendar'} className={`btn ${tab === 'calendar' ? 'btn-primary' : ''}`} onClick={() => setTab('calendar')}>
            {t('planner.calendar')}
          </button>
        )}
        {tasksOn && (
          <button type="button" role="tab" aria-selected={tab === 'tasks'} className={`btn ${tab === 'tasks' ? 'btn-primary' : ''}`} onClick={() => setTab('tasks')}>
            {t('planner.tasks')}
          </button>
        )}
      </div>
      {tab === 'calendar' && calendarOn && <CalendarTab />}
      {tab === 'tasks' && tasksOn && <TasksTab />}
    </div>
  );
}

// ---------- calendar ----------

function monthStart(offset: number): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + offset, 1);
}

/** Local date + time inputs to an instant. */
function toInstant(date: string, time: string): Date {
  return new Date(`${date}T${time || '00:00'}`);
}

function todayInput(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function CalendarTab() {
  const { i18n } = useApp();
  const { t, locale } = i18n;
  const chat = useChat();
  const options = useScopeOptions('event');
  const [offset, setOffset] = useState(0);
  const [events, setEvents] = useState<EventDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', scope: 'personal', date: todayInput(), start: '09:00', end: '10:00', allDay: false, location: '', description: '' });
  const from = monthStart(offset);
  const rangeQuery = `from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(monthStart(offset + 1).toISOString())}`;

  // Refetch when the month changes or the server says something in one of our scopes changed.
  useEffect(() => {
    let alive = true;
    api<{ events: EventDto[] }>('GET', `/calendar/events?${rangeQuery}`).then(
      (r) => alive && setEvents(r.events),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [rangeQuery, chat.plannerVersion, t]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      const r = await api<{ events: EventDto[] }>('GET', `/calendar/events?${rangeQuery}`);
      setEvents(r.events);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const create = (e: FormEvent) => {
    e.preventDefault();
    const scope = options.find((o) => o.key === form.scope)?.scope ?? { type: 'personal' as const };
    const startsAt = toInstant(form.date, form.allDay ? '00:00' : form.start);
    const endsAt = form.allDay ? new Date(toInstant(form.date, '23:59').getTime() + 59_000) : toInstant(form.date, form.end);
    void run(async () => {
      await api('POST', '/calendar/events', {
        scope,
        title: form.title,
        location: form.location,
        description: form.description,
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        allDay: form.allDay,
      });
      setForm((f) => ({ ...f, title: '', location: '', description: '' }));
    });
  };

  const days = useMemo(() => {
    const byDay = new Map<string, EventDto[]>();
    for (const ev of events ?? []) {
      const key = new Date(ev.startsAt).toDateString();
      byDay.set(key, [...(byDay.get(key) ?? []), ev]);
    }
    return [...byDay.entries()];
  }, [events]);

  const dayFmt = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const monthFmt = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });

  return (
    <>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      <section className="card stack" aria-labelledby="cal-month">
        <div className="row">
          <button className="btn" type="button" onClick={() => setOffset((o) => o - 1)} aria-label={t('planner.prevMonth')}>
            ‹
          </button>
          <h2 id="cal-month" style={{ margin: 0 }}>
            {monthFmt.format(from)}
          </h2>
          <button className="btn" type="button" onClick={() => setOffset((o) => o + 1)} aria-label={t('planner.nextMonth')}>
            ›
          </button>
          <span className="spacer" />
          {offset !== 0 && (
            <button className="btn" type="button" onClick={() => setOffset(0)}>
              {t('planner.today')}
            </button>
          )}
        </div>
        {events === null && !error && <p className="muted">{t('app.loading')}</p>}
        {events?.length === 0 && <p className="muted">{t('planner.noEvents')}</p>}
        {days.map(([key, list]) => (
          <div key={key} className="stack" style={{ gap: 'var(--space-2)' }}>
            <h3 style={{ margin: 0 }}>{dayFmt.format(new Date(key))}</h3>
            {list.map((ev) => (
              <EventItem key={ev.id} ev={ev} time={ev.allDay ? t('planner.allDay') : `${timeFmt.format(new Date(ev.startsAt))}–${timeFmt.format(new Date(ev.endsAt))}`} run={run} />
            ))}
          </div>
        ))}
      </section>

      <form className="card stack" onSubmit={create}>
        <h2>{t('planner.newEvent')}</h2>
        <div className="field">
          <label htmlFor="ev-title">{t('planner.eventTitle')}</label>
          <input id="ev-title" className="input" required maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="ev-scope">{t('planner.where')}</label>
          <select id="ev-scope" className="input" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
            {options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="ev-date">{t('planner.date')}</label>
            <input id="ev-date" className="input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          {!form.allDay && (
            <>
              <div className="field">
                <label htmlFor="ev-start">{t('planner.start')}</label>
                <input id="ev-start" className="input" type="time" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="ev-end">{t('planner.end')}</label>
                <input id="ev-end" className="input" type="time" required value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
              </div>
            </>
          )}
        </div>
        <label className="check">
          <input type="checkbox" checked={form.allDay} onChange={(e) => setForm({ ...form, allDay: e.target.checked })} />
          <span>{t('planner.allDay')}</span>
        </label>
        <div className="field">
          <label htmlFor="ev-location">{t('planner.location')}</label>
          <input id="ev-location" className="input" maxLength={200} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="ev-description">{t('planner.description')}</label>
          <textarea id="ev-description" className="input" rows={2} maxLength={2000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <p className="hint">{t('planner.scopeNote')}</p>
        <div className="row">
          <span className="spacer" />
          <button className="btn btn-primary">{t('planner.addEvent')}</button>
        </div>
      </form>
    </>
  );
}

function EventItem({ ev, time, run }: { ev: EventDto; time: string; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const shared = ev.scope.type !== 'personal';
  const respond = (response: EventResponse | null) => run(() => api('PUT', `/calendar/events/${ev.id}/response`, { response }));
  return (
    <div className="list-item" style={{ alignItems: 'flex-start', opacity: ev.cancelled ? 0.6 : 1 }}>
      <span style={{ minWidth: 0 }} className="stack">
        <span>
          <strong style={{ textDecoration: ev.cancelled ? 'line-through' : undefined }}>{ev.title}</strong>{' '}
          {ev.cancelled && <span className="badge">{t('planner.cancelled')}</span>}
        </span>
        <span className="muted">
          {time}
          {ev.location && ` · ${ev.location}`}
          {` · ${ev.scope.name ?? t('planner.personal')}`}
        </span>
        {ev.description && <span>{ev.description}</span>}
        {shared && (
          <span className="muted">
            {t('planner.responseCounts', { going: ev.responses.going, maybe: ev.responses.maybe, declined: ev.responses.declined })}
          </span>
        )}
        {shared && !ev.cancelled && (
          <span className="row" role="group" aria-label={t('planner.yourResponse')}>
            {(['going', 'maybe', 'declined'] as const).map((r) => (
              <button
                key={r}
                type="button"
                className={`btn ${ev.myResponse === r ? 'btn-primary' : ''}`}
                aria-pressed={ev.myResponse === r}
                onClick={() => void respond(ev.myResponse === r ? null : r)}
              >
                {t(`planner.response.${r}` as MessageKey)}
              </button>
            ))}
          </span>
        )}
      </span>
      <span className="spacer" />
      {ev.canEdit && (
        <button
          className="link-button"
          type="button"
          onClick={() => {
            if (window.confirm(shared ? t('planner.cancelConfirm') : t('planner.deleteConfirm'))) void run(() => api('DELETE', `/calendar/events/${ev.id}`));
          }}
        >
          {shared ? t('planner.cancel') : t('planner.delete')}
        </button>
      )}
    </div>
  );
}

// ---------- tasks ----------

function TasksTab() {
  const { i18n } = useApp();
  const { t, locale } = i18n;
  const chat = useChat();
  const options = useScopeOptions('task');
  const [view, setView] = useState('mine');
  const [includeDone, setIncludeDone] = useState(false);
  const [tasks, setTasks] = useState<TaskDto[] | null>(null);
  const [people, setPeople] = useState<AssignableDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', scope: 'personal', due: '', assigneeId: '' });

  const query = view === 'mine' ? `view=mine` : `view=scope&scopeType=${view.split(':')[0]}&scopeId=${view.split(':')[1]}`;
  const load = () => api<{ tasks: TaskDto[] }>('GET', `/tasks?${query}&includeDone=${includeDone}`).then((r) => setTasks(r.tasks));

  useEffect(() => {
    let alive = true;
    api<{ tasks: TaskDto[] }>('GET', `/tasks?${query}&includeDone=${includeDone}`).then(
      (r) => alive && setTasks(r.tasks),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [query, includeDone, chat.plannerVersion, t]);

  // People the new task may be assigned to (only for shared scopes).
  useEffect(() => {
    if (form.scope === 'personal') return;
    const [type, id] = form.scope.split(':');
    let alive = true;
    api<{ people: AssignableDto[] }>('GET', `/tasks/assignable?scopeType=${type}&scopeId=${id}`).then(
      (r) => alive && setPeople(r.people),
      () => alive && setPeople([]),
    );
    return () => {
      alive = false;
    };
  }, [form.scope]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const create = (e: FormEvent) => {
    e.preventDefault();
    const scope = options.find((o) => o.key === form.scope)?.scope ?? { type: 'personal' as const };
    void run(async () => {
      await api('POST', '/tasks', {
        scope,
        title: form.title,
        dueAt: form.due ? toInstant(form.due, '23:59').toISOString() : null,
        assigneeId: scope.type === 'personal' || !form.assigneeId ? null : form.assigneeId,
      });
      setForm((f) => ({ ...f, title: '', due: '' }));
    });
  };

  const dateFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  // Fixed at mount: "overdue" is a hint, not a live clock.
  const [now] = useState(() => Date.now());
  const viewOptions = [{ key: 'mine', label: t('planner.myTasks') }, ...options.filter((o) => o.key !== 'personal').map((o) => ({ key: o.key, label: o.label }))];

  return (
    <>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      <section className="card stack" aria-labelledby="task-list">
        <h2 id="task-list" className="visually-hidden">
          {t('planner.tasks')}
        </h2>
        <div className="row">
          <label className="visually-hidden" htmlFor="task-view">
            {t('planner.show')}
          </label>
          <select id="task-view" className="input" style={{ width: 'auto' }} value={view} onChange={(e) => setView(e.target.value)}>
            {viewOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
          <label className="check">
            <input type="checkbox" checked={includeDone} onChange={(e) => setIncludeDone(e.target.checked)} />
            <span>{t('planner.showDone')}</span>
          </label>
        </div>
        {tasks === null && !error && <p className="muted">{t('app.loading')}</p>}
        {tasks?.length === 0 && <p className="muted">{t('planner.noTasks')}</p>}
        {tasks?.map((task) => (
          <div key={task.id} className="toggle-row">
            <label className="check" style={{ minWidth: 0 }}>
              <input
                type="checkbox"
                checked={task.done}
                disabled={!task.canComplete}
                onChange={(e) => void run(() => api('PATCH', `/tasks/${task.id}`, { done: e.target.checked }))}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{ textDecoration: task.done ? 'line-through' : undefined }}>{task.title}</span>
                <br />
                <span className="muted">
                  {task.scope.name ?? t('planner.personal')}
                  {task.assignee && ` · ${t('planner.assignedTo', { name: task.assignee.displayName })}`}
                  {task.dueAt && (
                    <>
                      {' · '}
                      <span className={!task.done && Date.parse(task.dueAt) < now ? 'badge' : undefined}>{t('planner.due', { date: dateFmt.format(new Date(task.dueAt)) })}</span>
                    </>
                  )}
                </span>
              </span>
            </label>
            <span className="spacer" />
            {task.canEdit && (
              <button
                className="link-button"
                type="button"
                onClick={() => {
                  if (window.confirm(t('planner.deleteTaskConfirm'))) void run(() => api('DELETE', `/tasks/${task.id}`));
                }}
              >
                {t('planner.delete')}
              </button>
            )}
          </div>
        ))}
      </section>

      <form className="card stack" onSubmit={create}>
        <h2>{t('planner.newTask')}</h2>
        <div className="field">
          <label htmlFor="task-title">{t('planner.taskTitle')}</label>
          <input id="task-title" className="input" required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="task-scope">{t('planner.where')}</label>
            <select id="task-scope" className="input" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value, assigneeId: '' })}>
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {form.scope !== 'personal' && (
            <div className="field">
              <label htmlFor="task-assignee">{t('planner.assignee')}</label>
              <select id="task-assignee" className="input" value={form.assigneeId} onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}>
                <option value="">{t('planner.unassigned')}</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="task-due">{t('planner.dueDate')}</label>
            <input id="task-due" className="input" type="date" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} />
          </div>
        </div>
        <div className="row">
          <span className="spacer" />
          <button className="btn btn-primary">{t('planner.addTask')}</button>
        </div>
      </form>
    </>
  );
}

